import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import PieChart from '/@/components/bayer/pie-chart';
import Tabs from '/@/components/bayer/tabs';
import { useRVDashboards } from '/@/hooks/dashboards';
import { _ } from '/@/locale';

const RVsStatsDashboardCard = ({ userNodeId }: { userNodeId: number }) => {
    const rvsUserNodesQuery = useRVDashboards({ userNodeId });
    const [selectedTab, setSelectedTab] = useState<'growers-by-rv' | 'fields-by-rv'>('growers-by-rv');

    const growersData = rvsUserNodesQuery.data?.growersByRv || [];
    const fieldsData = rvsUserNodesQuery.data?.fieldsByRv || [];

    // Determine which data to display based on selected tab
    const currentData = useMemo(() => {
        const data = selectedTab === 'growers-by-rv' ? growersData : fieldsData;
        return data.toSorted((a, b) => b.count - a.count);
    }, [selectedTab, growersData, fieldsData]);

    const totalCount =
        selectedTab === 'growers-by-rv'
            ? rvsUserNodesQuery.data?.totalCount || 0
            : rvsUserNodesQuery.data?.totalFieldsCount || 0;

    const subtitleText =
        selectedTab === 'growers-by-rv'
            ? _('Cantidad de productores agregados por RV')
            : _('Cantidad de lotes gestionados por Rv');

    return (
        <DashboardCard isLoading={rvsUserNodesQuery.isLoading}>
            <Stack direction='column' spacing={3}>
                <Stack spacing={1}>
                    <Tabs
                        options={[
                            { label: _('Productores por RV'), value: 'growers-by-rv' },
                            { label: _('Lotes por RV'), value: 'fields-by-rv' },
                        ]}
                        value={selectedTab}
                        setValue={(value) => setSelectedTab(value as 'growers-by-rv' | 'fields-by-rv')}
                        variant='chips'
                    />
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {subtitleText}
                    </Typography>
                </Stack>
                {!rvsUserNodesQuery.isLoading ? (
                    // biome-ignore lint/complexity/noUselessFragments: looks better than a ternary
                    <Box>
                        {currentData.length > 0 ? (
                            <PieChart
                                data={currentData}
                                tooltipLabel={selectedTab === 'growers-by-rv' ? _('productores') : _('lotes')}
                                totalCount={totalCount}
                                sx={{ maxWidth: '200px', mx: 'auto' }}
                            />
                        ) : (
                            !rvsUserNodesQuery.isLoading && (
                                <Stack alignItems='center' justifyContent='center' minHeight={300}>
                                    <Typography variant='body2' color='primary.mui.inactive'>
                                        {_('No hay datos disponibles')}
                                    </Typography>
                                </Stack>
                            )
                        )}
                    </Box>
                ) : (
                    <Skeleton variant='rectangular' height={200} />
                )}
            </Stack>
        </DashboardCard>
    );
};

export default RVsStatsDashboardCard;
